import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import createMovementsStyles from "./../styles/CreateMovements.module.scss";
import GridWrapper from '../../../../../components/controls/GridWrapper';
import StoreSelectorPanel from '../../../../../components/storeSelectorPanel/StoreSelectorPanel';
import { IdTableVariant, DocumentType } from '../../../../../@types/enumsGlobal';
import { onCanChangeData } from '../../../../../system/functions/onCanChangeData';
import { ICostInfoDTO, IEntitySimpleDTO } from '../../../../../libs/coreapi-dto/@types/common';
import { useAppContext } from '../../../../../system/providers/appContextProvider';
import { IMovementCreateDTO } from '../../../../../libs/coreapi-dto/documents/movement';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { DateTime } from 'luxon';
import { RequestForMovementItemsGrid } from '../../../RequestForMovement/RequestForMovementItems/RequestForMovementItemsGrid';
import RequestForMovementItemsGridPluginSettings from '../../../RequestForMovement/RequestForMovementItems/RequestForMovementItemsGridPluginSettings';
import FlexRow from '../../../../../components/controls/FlexRow';
import PositionEditPanel from '../../../../../components/commandsPanels/PositionEditPanel';
import { IMovementItemCreateDTO, IMovementItemEditDTO } from '../../../../../libs/coreapi-dto/documents/movementItem';
import { LotDataProvider } from '../../../../../Services/DataProviders/LotDataProvider';
import { ILotDTO } from '../../../../../libs/coreapi-dto/accounting/lot';
import useOperativeReserve from '../../../../../components/lotSelector/useOperativeReserve';
import useGridFilter from '../../../../../system/hooks/useGridFilter';
import LotsSelectorModal from '../../../../Accounting/Lots/LotsSelectorModal';
import styles from "../../../../styles/index.module.scss";
import { ChangeableGrid } from '../../../../../components/grids/changeableGrid/ChangeableGrid';
import { getStyleCell } from '../../../../../system/functions/getStyleCell';
import { useTimeout } from '../../../../../system/hooks/useTimeout';
import { addValueToArray } from '../../../../../system/functions/addValToArray';
import { roundMoneyFormat } from '../../../../../system/functions/round';
import { MovementCreatorItemModal } from '../MovementCreatorItem/MovementCreatorItemModal';
import { ICreateItem } from '../MovementUpdateForm';
import { MovementUpdateItemModal } from '../MovementCreatorItem/MovementUpdateItemModal';
import { MovementDataProvider } from '../../../../../Services/DataProviders/MovementDataProvider';
import { useTabsContext } from '../../../../../system/providers/tabsProvider';
import MovementItemsModalGridPluginSettings from './MovementItemsModalGridPluginSettings';
import { GoodsDataProvider } from '../../../../../Services/DataProviders/GoodsDataProvider';
import GridSelectorButton from './GridSelectorButton';
import { RequestForMovementDataProvider } from '../../../../../Services/DataProviders/RequestForMovementDataProvider';
import { IRequestForMovementGetDTO } from '../../../../../libs/coreapi-dto/documents/requestForMovement';
import BaseDocumentCreator from '../../../../../components/documents/baseDocumentCreator';
import { DocumentOperationSpinner } from '../../../../../components/DocumentOperationSpinner/DocumentOperationSpinner';

interface IMovementFromRequestCreatorViewProps {
    idGlobal: string;
    requestId: string;
    variant: GridStateType;
    createdCallback?: <T>(message?: T) => void;
    permission?: IPermission;
}

interface IBaseFields {
    contractorFrom: IEntitySimpleDTO;
    contractorTo: IEntitySimpleDTO;
    storeFrom: IEntitySimpleDTO;
    storeTo: IEntitySimpleDTO;
}

interface ICreatorItem {
    show: boolean;
    item: ILotDTOWithAdditionalData | null;
    editItem?: IMovementItemEditDTO | null;
}

interface IDisplayItem extends IDocumentItemCostInfoViewDTO {
    idGlobal: string;
    idLotFromGlobal: string;
    idRequestItemGlobal: string;
    goodsName: string;
    scalingRatioDisplayName: string;
    actualQuantity: number;
    quantity: number;
    goodsCode: string;
    producer: string;
    isKiz: boolean;
    countKizScan: number | undefined;
    numerator: number;
    denominator: number;
    supplierFrom?: string;
    supplierTo?: string;
    supplier?: string;
    dateCreated: DateTime;
    isOsu?: boolean;
    gosContractName: string;
    mnn?: string;
    tradeName?: string;
    cureFormName?: string;
    dosage?: string;
    nFv?: number;
}

interface IMovementItemWithRequestLink extends IMovementItemCreateDTO {
    idRequestItemGlobal: string;
}

interface IChangeDataGrid {
    propertyName: string;
    value: string | number | boolean;
    oldValue?: string | number;
    idGlobal: string;
}

interface ILotDTOWithAdditionalData extends ILotDTO {
    mnn?: string;
    tradeName?: string;
    cureFormName?: string;
    dosage?: string;
    nFv?: number;
}

export const MovementFromRequestCreatorView: FC<IMovementFromRequestCreatorViewProps> = ({
    idGlobal,
    requestId,
    createdCallback,
}) => {
    const idTable = IdTableVariant.Movement;

    const appContext = useAppContext();
    const tabsContext = useTabsContext();
    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.requestForMovement." + value);

    const lotDP = new LotDataProvider(appContext.coreApiService);
    const goodsDP = new GoodsDataProvider(appContext.coreApiService);
    const movementDP = new MovementDataProvider(appContext.coreApiService);
    const requestForMovementDP = new RequestForMovementDataProvider(appContext.coreApiService);

    const operativeReserve = useOperativeReserve();
    const [setTimer, clearTimer] = useTimeout();
    const [gridFilterLot, dispatchGridFilterLot] = useGridFilter();
    const [gridFilter, dispatchGridFilter] = useGridFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [requestData, setRequestData] = useState<IRequestForMovementGetDTO | null>(null);
    const [data, setData] = useState<IMovementCreateDTO>({} as IMovementCreateDTO);

    const [displayItems, setDisplayItems] = useState<IDisplayItem[]>([]);

    const [itemsFilter, setItemsFilter] = useState<IItemFilter[]>([]);
    const [itemsValidation, setItemsValidation] = useState<IValidationItem[]>([]);
    const [selectedRequestItem, setSelectedRequestItem] = useState<ISelectGridRow>();

    const [selectedItem, setSelectedItem] = useState<IGridRow>();

    const [creatorItem, setCreatorItem] = useState<ICreatorItem>({
        show: false,
        item: null,
    });
    const [baseFields, setBaseFields] = useState<IBaseFields>({
        contractorFrom: {} as IEntitySimpleDTO,
        contractorTo: {} as IEntitySimpleDTO,
        storeFrom: {} as IEntitySimpleDTO,
        storeTo: {} as IEntitySimpleDTO,
    });

    const [totalCount] = useState<number>(0);

    // gridId для предотвращения перерисовок
    const [requestItemsGridId] = useState(() => uuidv4());
    const [movementItemsGridId] = useState(() => uuidv4());

    // Позиции для отображения в нижней таблице
    const filteredDisplayItems = useMemo(() => {
        if (!selectedRequestItem?.idGlobal) return [];
        return displayItems.filter(item => item.idRequestItemGlobal === selectedRequestItem.idGlobal);
    }, [displayItems, selectedRequestItem?.idGlobal]);

    // Заявки, у которых есть добавленные позиции перемещения
    const requestItemIdsWithMovementItems = useMemo(() => {
        return new Set(displayItems.map(item => item.idRequestItemGlobal));
    }, [displayItems]);

    // Функция для подсветки строк верхнего грида
    const getRequestItemRowStyle = useCallback((row: IGridRow): string | null => {
        if (requestItemIdsWithMovementItems.has(row.idGlobal) && row.idGlobal !== selectedRequestItem?.idGlobal) {
            return createMovementsStyles.rowWithItems;
        }
        return null;
    }, [requestItemIdsWithMovementItems, selectedRequestItem?.idGlobal]);

    useEffect(() => {
        if (requestId) {
            setIsLoading(true);
            requestForMovementDP.getById(requestId, (detailedRequest) => {
                if (detailedRequest) {
                    setRequestData(detailedRequest);
                }
                setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }
    }, [requestId]);

    useEffect(() => {
        if (requestData) {
            initializeDataFromRequest();
        }
    }, [requestData]);

    useEffect(() => {
        setupGridFilters();
    }, [selectedRequestItem, baseFields.storeFrom?.idGlobal]);

    useEffect(() => {
        syncDataWithBaseFields();
    }, [baseFields.storeFrom, baseFields.storeTo]);

    const initializeDataFromRequest = useCallback(() => {
        if (!requestData) return;

        const documentNumber = requestData.mnemocode || '';
        const initialData: IMovementCreateDTO = {
            idGlobal: idGlobal || uuidv4(),
            baseDocumentInfo: {
                idTable: 35,
                idDocument: requestData.idGlobal
            },
            documentDate: DateTime.now(),
            idStoreFromGlobal: requestData.supplierStore.idGlobal,
            idStoreToGlobal: requestData.store?.idGlobal,
            userComment: `Создано из заявки ${requestData.mnemocode}`,
            isNewBarcode: true,
            isCrosspharmacy: true,
            idGosContractGlobal: undefined,
            idMovementReasonGlobal: undefined,
            orderNumber: documentNumber,
            reserveDate: requestData.documentDate || DateTime.now().toISO(),
            idDisposalReasonGismtGlobal: undefined,
            isWithdrawalGismtForUnscannedLots: false,
            items: [],
        } as IMovementCreateDTO;

        setData(initialData);
        setBaseFields({
            contractorFrom: requestData.supplier || {} as IEntitySimpleDTO,
            contractorTo: requestData.contractor || {} as IEntitySimpleDTO,
            storeFrom: requestData.supplierStore || {} as IEntitySimpleDTO,
            storeTo: requestData.store || {} as IEntitySimpleDTO,
        });

    }, [requestData, idGlobal]);

    const setupGridFilters = useCallback(() => {
        dispatchGridFilterLot({ type: "deleteColumnFilters", payload: [] });

        if (baseFields.storeFrom?.idGlobal) {
            dispatchGridFilterLot({
                type: "addColumnFilter",
                payload: {
                    name: "idStoreGlobal",
                    operator: "Eq",
                    value: baseFields.storeFrom.idGlobal,
                },
            });
        }

        if (selectedRequestItem) {
            const substanceName = selectedRequestItem.cells?.find(cell => cell.propertyName === 'substanceName')?.value;
            const tradeName = selectedRequestItem.cells?.find(cell => cell.propertyName === 'tradeName')?.value;

            const hasSubstanceName = substanceName && substanceName.toString().trim() !== '';
            const hasTradeName = tradeName && tradeName.toString().trim() !== '';

            dispatchGridFilterLot({ type: "deleteColumnFilter", payload: "mnn" });
            dispatchGridFilterLot({ type: "deleteColumnFilter", payload: "tradeNameRus" });

            if (hasSubstanceName && !hasTradeName) {
                dispatchGridFilterLot({
                    type: "addColumnFilter",
                    payload: { name: "mnn", operator: "Eq", value: substanceName },
                });
            } else if (hasTradeName && !hasSubstanceName) {
                dispatchGridFilterLot({
                    type: "addColumnFilter",
                    payload: { name: "tradeNameRus", operator: "Eq", value: tradeName },
                });
            } else if (hasTradeName && hasSubstanceName) {
                dispatchGridFilterLot({
                    type: "addColumnFilter",
                    payload: { name: "mnn", operator: "Eq", value: substanceName },
                });
            }
        }
    }, [selectedRequestItem, baseFields.storeFrom?.idGlobal]);

    const syncDataWithBaseFields = useCallback(() => {
        setData(prevData => ({
            ...prevData,
            idStoreFromGlobal: baseFields.storeFrom?.idGlobal,
            idStoreToGlobal: baseFields.storeTo?.idGlobal,
        }));
    }, [baseFields.storeFrom, baseFields.storeTo]);

    const addItem = (value: IChangeDataGrid) => {

        let quantity = (value.value as number)
        let indexItem = data.items.findIndex((item) => item.idGlobal === value.idGlobal);
        let takeDataObj = data.items.find((item) => item.idGlobal === value.idGlobal) as IMovementItemCreateDTO;

        let indexDisplayItem = displayItems.findIndex((item) => item.idGlobal === value.idGlobal);
        let takeDisplayObj = displayItems.find((item) => item.idGlobal === value.idGlobal) as IDisplayItem;

        let newItem: IMovementItemCreateDTO = {
            ...takeDataObj,
            retailCostInfo: {
                ...takeDataObj.retailCostInfo,
                vat: takeDataObj.retailCostInfo.vat,
                sum: roundMoneyFormat(takeDataObj.retailCostInfo.price * quantity),
                vatSum: roundMoneyFormat(takeDataObj.retailCostInfo.vatPrice * quantity),
                sumIncVat: roundMoneyFormat(takeDataObj.retailCostInfo.priceIncVat * quantity),
            } as ICostInfoDTO,
            supplierCostInfo: {
                ...takeDataObj.supplierCostInfo,
                vat: takeDataObj.supplierCostInfo.vat,
                sum: roundMoneyFormat(takeDataObj.supplierCostInfo.price * quantity),
                vatSum: roundMoneyFormat(takeDataObj.supplierCostInfo.vatPrice * quantity),
                sumIncVat: roundMoneyFormat(takeDataObj.supplierCostInfo.priceIncVat * quantity),
            } as ICostInfoDTO,
            quantity: quantity,
        };

        let newDisplayItem: IDisplayItem = {
            ...takeDisplayObj,
            supplierVat: newItem.supplierCostInfo.vat,
            supplierPrice: newItem.supplierCostInfo.price,
            supplierVatPrice: newItem.supplierCostInfo.vatPrice,
            supplierPriceIncVat: newItem.supplierCostInfo.priceIncVat,
            supplierSum: newItem.supplierCostInfo.sum,
            supplierVatSum: newItem.supplierCostInfo.vatSum,
            supplierSumIncVat: newItem.supplierCostInfo.sumIncVat,
            supplierAdprice: newItem.supplierCostInfo.adprice,

            retailVat: newItem.retailCostInfo.vat,
            retailPrice: newItem.retailCostInfo.price,
            retailVatPrice: newItem.retailCostInfo.vatPrice,
            retailPriceIncVat: newItem.retailCostInfo.priceIncVat,
            retailSum: newItem.retailCostInfo.sum,
            retailVatSum: newItem.retailCostInfo.vatSum,
            retailSumIncVat: newItem.retailCostInfo.sumIncVat,
            retailAdprice: newItem.retailCostInfo.adprice,
            quantity: quantity,
        };

        setData({
            ...data,
            items: addValueToArray(data.items, indexItem, newItem),
        });
        setDisplayItems([...addValueToArray(displayItems, indexDisplayItem, newDisplayItem)]);
    };

    const calcRow = (value: IChangeDataGrid) => {
        if (value.propertyName === "quantity") {
            let idLot: string = data.items.find((x) => x.idGlobal === value.idGlobal)?.idLotFromGlobal as string;
            operativeReserve.send(
                {
                    idLot: idLot,
                    idDocument: data.idGlobal as string,
                    idTable: IdTableVariant.Movement,
                    idDocumentItem: value.idGlobal,
                    quantity: value.value as number,
                },
                () => addItem(value)
            );
        } else {
            addItem(value);
        }
    }

    const redirectToMovementDocument = (movementId: string, movementNumber: string) => {
        tabsContext.openChild(
            'movement_creator_plugin',
            'edit',
            movementId,
            () => { },
            movementNumber
        );
    };

    const prepareDataForServer = (dto: IMovementCreateDTO): IMovementCreateDTO => {
        return {
            ...dto,
            items: dto.items.map(item => {
                const { idRequestItemGlobal, ...rest } = item as IMovementItemWithRequestLink;
                return rest as IMovementItemCreateDTO;
            })
        };
    };

    const sendCreateMovementRequest = (dto: IMovementCreateDTO): Promise<string> => {
        const preparedData = prepareDataForServer(dto);
        return new Promise((resolve) => {
            movementDP.create(
                preparedData,
                (idGlobal: string) => {
                    resolve(idGlobal);
                    const requestNumber = requestData?.mnemocode
                    redirectToMovementDocument(idGlobal, `${requestNumber}`)
                },
                undefined,
            );
        });
    };

    const onOk = () => {
        if (!baseFields.storeFrom?.idGlobal || !baseFields.storeTo?.idGlobal || !baseFields.contractorTo?.idGlobal || !baseFields.contractorFrom?.idGlobal) {
            return;
        }
        setIsSubmitting(true);
        sendCreateMovementRequest(data).then(() => {
            setIsSubmitting(false);
            createdCallback?.();
            tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
        });
    };

    const onSave = () => {
        if (!baseFields.storeFrom?.idGlobal || !baseFields.storeTo?.idGlobal || !baseFields.contractorTo?.idGlobal || !baseFields.contractorFrom?.idGlobal) {
            return;
        }
        setIsSubmitting(true);
        const preparedData = prepareDataForServer(data);
        movementDP.create(
            preparedData,
            (movementIdGlobal: string) => {
                setIsSubmitting(false);
                createdCallback?.();
                const requestNumber = requestData?.mnemocode;
                redirectToMovementDocument(movementIdGlobal, `${requestNumber}`);
            },
            undefined,
            () => {
                setIsSubmitting(false);
            }
        );
    };

    const onCancel = () => {
        tabsContext.currentTab && tabsContext.close(tabsContext.currentTab);
    };

    const isFormValid = Boolean(
        baseFields.storeFrom?.idGlobal &&
        baseFields.storeTo?.idGlobal &&
        baseFields.contractorTo?.idGlobal &&
        baseFields.contractorFrom?.idGlobal
    );

    const formatRequestItemDisplayName = (
        item?: {
            substanceNameLat?: string | null;
            tradeNameLat?: string | null;
            cureFormName?: string | null;
            dosage?: string | null;
            quantity?: number;
            nFv?: string;
        },
        prefix: string = "Заявка:"
    ): string => {
        if (!item) return `${prefix} —`;

        const parts: string[] = [];

        if (item.substanceNameLat) {
            parts.push(item.substanceNameLat);
        } else if (item.tradeNameLat) {
            parts.push(item.tradeNameLat);
        }

        if (item.quantity != null) {
            parts.push(String(item.quantity));
        }
        if (item.nFv != null) {
            parts.push(String(item.nFv));
        }

        if (item.cureFormName) {
            parts.push(item.cureFormName);
        }
        if (item.dosage) {
            parts.push(item.dosage);
        }

        return `${prefix} ${parts.join(", ")}`;
    };

    if (isLoading) {
        return (
            <div>
                <DocumentOperationSpinner xl data-testid="movement-from-request-loading" />
            </div>
        );
    }

    if (!requestData) {
        return (
            <div>
                <p>Не удалось загрузить данные заявки</p>
            </div>
        );
    }

    return (
        <BaseDocumentCreator
            valid={true}
            ok={{
                onClick: onOk,
                disabled: !isFormValid || isSubmitting,
                sendRequestGlobal: isSubmitting,
            }}
            save={{
                onClick: onSave,
                disabled: !isFormValid || isSubmitting,
                sendRequestGlobal: isSubmitting,
            }}
            cancel={{ onClick: onCancel }}
        >
            <div className={createMovementsStyles.glwrapper}>
                <GridWrapper cols={2}>
                    <StoreSelectorPanel
                        required
                        notAutoComplete={true}
                        contractor={{
                            label: baseT('contractorFrom'),
                            value: baseFields.contractorFrom,
                            onSelect: undefined,
                            onClear: undefined,
                            disabled: true,
                            treeViewCheckDirectoryType: "pharmacy",
                        }}
                        store={{
                            label: baseT('storeFrom'),
                            value: baseFields.storeFrom,
                            onSelect: (value) => {
                                setBaseFields(prev => ({
                                    ...prev,
                                    storeFrom: value as IEntitySimpleDTO
                                }));
                            },
                            onClear: () => {
                                setBaseFields(prev => ({
                                    ...prev,
                                    storeFrom: {} as IEntitySimpleDTO
                                }));
                            },
                            disabled: false,
                            isAcceptRequestForMovement: true,
                        }}
                        change={onCanChangeData(0)}
                        documentStore={baseFields.storeFrom}
                    />
                    <StoreSelectorPanel
                        required
                        idTable={idTable}
                        notAutoComplete={true}
                        contractor={{
                            label: baseT('contractorTo'),
                            value: baseFields.contractorTo,
                            disabled: true,
                            treeViewCheckDirectoryType: 'pharmacy'
                        }}
                        store={{
                            label: baseT('storeTo'),
                            value: baseFields.storeTo,
                            disabled: true,
                        }}
                        documentStore={baseFields.storeTo}
                    />
                </GridWrapper>

                <RequestForMovementItemsGrid
                    gridId={requestItemsGridId}
                    plugin={RequestForMovementItemsGridPluginSettings}
                    onSelect={(row: ISelectGridRow | undefined) => {
                        setSelectedRequestItem(row);
                        setSelectedItem(undefined);
                    }}
                    forceLoadRequestId={requestData?.idGlobal}
                    getStyleRow={getRequestItemRowStyle}
                />

                <div className={styles.gridWrapper}>
                    <FlexRow className={createMovementsStyles.modalMovementModeField}>
                        <PositionEditPanel
                            edit={{
                                onClick: () => {
                                    if (!selectedItem?.idGlobal) return;

                                    const editItem = data.items.find((x) => x.idGlobal === selectedItem.idGlobal);

                                    if (!editItem) { return };
                                    if (!editItem.idLotFromGlobal) return;

                                    lotDP.getById(editItem.idLotFromGlobal, (item: ILotDTO) => {
                                        if (!item) return;
                                        setCreatorItem({ show: true, item: item, editItem: editItem as IMovementItemCreateDTO });
                                    });
                                },
                                disabled: selectedItem ? false : true,
                            }}
                            delete={{
                                onClick: () => {
                                    if (selectedItem) {
                                        let item = data.items.find((x) => x.idGlobal === selectedItem.idGlobal);

                                        if (item) {
                                            operativeReserve.delete(
                                                {
                                                    idLot: item.idLotFromGlobal,
                                                    idTable: IdTableVariant.Movement,
                                                    idDocument: data.idGlobal as string,
                                                    idDocumentItem: item.idGlobal,
                                                },
                                                () => {
                                                    setData({ ...data, items: data.items.filter((x) => x.idGlobal !== selectedItem.idGlobal) });
                                                    setDisplayItems([...displayItems.filter((x) => x.idGlobal !== selectedItem.idGlobal)]);

                                                    const itemsValid = itemsValidation.filter((item) => item.idGlobal !== selectedItem?.idGlobal);
                                                    const itemsFilterValid = itemsFilter.filter((item) => item.idItem !== selectedItem?.idGlobal);
                                                    setItemsValidation(itemsValid)
                                                    setItemsFilter(itemsFilterValid)
                                                    setSelectedItem(undefined);
                                                }
                                            );
                                        }
                                    }
                                },
                                disabled: selectedItem ? false : true,
                            }}
                        />
                        <GridSelectorButton
                            disabled={selectedRequestItem === undefined}
                            gridFilter={gridFilterLot}
                            dispatchGridFilter={dispatchGridFilterLot}
                            selectorModalJsx={LotsSelectorModal}
                            className={createMovementsStyles.modalMovementModeField}
                            onlyGridDisplayName={formatRequestItemDisplayName(selectedRequestItem)}
                            id={"buttonGoodsSearch"}
                            inline={true}
                            placeholder={baseT('buttonSearch')}
                            onSelect={(value) => {
                                if (!value?.idGlobal) return;
                                lotDP.getById(value.idGlobal, (lot: ILotDTO) => {
                                    if (!lot?.goods?.idGlobal) {
                                        lotDP.remains(lot?.goods?.idGlobal, baseFields.storeFrom.idGlobal as string, (remain) => {
                                            const basicItem: ILotDTOWithAdditionalData = {
                                                ...lot,
                                                quantityRem: remain
                                            };
                                            setCreatorItem({
                                                show: true,
                                                item: basicItem,
                                            });
                                        });
                                        return;
                                    }
                                    goodsDP.getById(lot.goods.idGlobal, (goods) => {
                                        if (!goods) {
                                            lotDP.remains(lot.goods.idGlobal, baseFields.storeFrom.idGlobal as string, (remain) => {
                                                const basicItem: ILotDTOWithAdditionalData = {
                                                    ...lot,
                                                    quantityRem: remain
                                                };
                                                setCreatorItem({
                                                    show: true,
                                                    item: basicItem,
                                                });
                                            });
                                            return;
                                        }
                                        lotDP.remains(lot.goods.idGlobal, baseFields.storeFrom.idGlobal as string, (remain) => {
                                            const itemWithAdditionalData: ILotDTOWithAdditionalData = {
                                                ...lot,
                                                ...(goods && {
                                                    mnn: goods.substance?.displayName || '',
                                                    tradeName: goods.tradeName?.displayName || '',
                                                    cureFormName: goods.cureForm?.displayName || '',
                                                    dosage: goods.dosage || '',
                                                    nFv: goods.nFv || 0
                                                })
                                            };

                                            setCreatorItem({
                                                show: true,
                                                item: itemWithAdditionalData,
                                            });
                                        });
                                    });
                                });
                            }}
                            onClear={() => { }}
                            idTable={IdTableVariant.Movement}
                        />
                    </FlexRow>
                    <div className={createMovementsStyles.gridWrapper}>
                        <ChangeableGrid
                            autoSelect
                            gridId={movementItemsGridId}
                            data={filteredDisplayItems}
                            documentType={DocumentType.movement}
                            filter={gridFilter}
                            totalCount={totalCount}
                            plugin={MovementItemsModalGridPluginSettings}
                            selectedItem={selectedItem}
                            itemsFilter={itemsFilter}
                            itemsValidation={itemsValidation}
                            setItemsValidation={(value) => {
                                const newItemsValidation = itemsValidation.map((item) => (item.idGlobal === value[0].idGlobal ? { ...item, ...value } : item))
                                setItemsValidation(newItemsValidation)
                            }}
                            getStyleCell={(cell, cells) => getStyleCell(cell, cells, DocumentType.movement)}
                            onSelect={(row) => setSelectedItem(row)}
                            onClick={(row) => setSelectedItem(row)}
                            onSort={(i) => {
                                dispatchGridFilter({
                                    type: "sort",
                                    payload: i.propertyName,
                                });
                            }}
                            onFilterDelete={(i) =>
                                dispatchGridFilter({
                                    type: "deleteColumnFilter",
                                    payload: i.propertyName,
                                })
                            }
                            onPageNumberChange={(n) => {
                                dispatchGridFilter({
                                    type: "changePageNumber",
                                    payload: { pageNumber: n },
                                });
                            }}
                            onNumberPerPageChange={(n) => {
                                dispatchGridFilter({
                                    type: "changeNumberPerPage",
                                    payload: { numberPerPage: n },
                                });
                            }}
                            onColumnEnter={(value: IChangeDataGrid) => clearTimer(() => calcRow(value))}
                            onChangeData={(value: IChangeDataGrid) => setTimer(() => calcRow(value))}
                            searching
                            isCrosspharmacy={false}
                            isDocumentProcess={false}
                        />
                    </div>
                </div>
                {creatorItem.show && creatorItem.item && !creatorItem?.editItem && (
                    <MovementCreatorItemModal
                        title={formatRequestItemDisplayName(selectedRequestItem, "Введите количество для заявки:")}
                        idDocumentGlobal={data.idGlobal as string}
                        idDocumentItemGlobal={creatorItem.editItem?.idGlobal ?? uuidv4()}
                        lot={creatorItem.item}
                        requiredQuantity={selectedRequestItem?.cells?.find(cell => cell.propertyName === 'quantity')?.value as number}
                        isWithdrawalGismtForUnscannedLots={false}
                        items={data.items}
                        ok={(createItem: ICreateItem) => {
                            const currentRequestItemId = selectedRequestItem?.idGlobal || '';
                            let filteredItems = data.items.filter((x) =>
                                !(x.idLotFromGlobal === createItem.item.idLotFromGlobal &&
                                    (x as IMovementItemWithRequestLink).idRequestItemGlobal === currentRequestItemId)
                            );
                            let filteredDisplayItemsList = displayItems.filter((x) =>
                                !(x.idLotFromGlobal === createItem.item.idLotFromGlobal &&
                                    x.idRequestItemGlobal === currentRequestItemId)
                            );
                            lotDP.remains(createItem.item.idGoodsGlobal, baseFields.storeFrom.idGlobal as string, (remain) => {
                                const displayItemWithAdditionalData: IDisplayItem = {
                                    ...createItem.displayItem,
                                    idRequestItemGlobal: currentRequestItemId,
                                    actualQuantity: remain,
                                    supplierFrom: baseFields.contractorFrom?.displayName || '',
                                    supplierTo: baseFields.contractorTo?.displayName || '',
                                    isKiz: createItem.item.isKiz as boolean,
                                    mnn: creatorItem.item?.mnn || '',
                                    tradeName: creatorItem.item?.tradeName || '',
                                    cureFormName: creatorItem.item?.cureFormName || '',
                                    dosage: creatorItem.item?.dosage || '',
                                    nFv: creatorItem.item?.nFv || 0
                                };

                                const itemWithRequestLink: IMovementItemWithRequestLink = {
                                    ...createItem.item,
                                    idRequestItemGlobal: currentRequestItemId
                                };

                                setData({ ...data, items: [...filteredItems, itemWithRequestLink] });
                                setDisplayItems([...filteredDisplayItemsList, { ...displayItemWithAdditionalData }]);
                                setItemsFilter([...itemsFilter, createItem.filter]);
                                setItemsValidation([...itemsValidation, { idGlobal: createItem.item.idGlobal, message: "", err: false }]);
                                setCreatorItem({ show: false, item: null });
                            })
                        }}
                        cancel={() => setCreatorItem({ show: false, item: null })}
                    />
                )}
                {creatorItem.show && creatorItem.item && creatorItem?.editItem && (
                    <MovementUpdateItemModal
                        lot={creatorItem.item}
                        kizCount={displayItems.find((x) => x.idGlobal === selectedItem?.idGlobal)?.countKizScan as number}
                        requiredQuantity={selectedRequestItem?.cells?.find(cell => cell.propertyName === 'quantity')?.value as number}
                        idDocumentGlobal={data.idGlobal as string}
                        idDocumentItemGlobal={creatorItem.editItem?.idGlobal}
                        movementItemEdit={creatorItem.editItem}
                        isWithdrawalGismtForUnscannedLots={false}
                        ok={(createItem: ICreateItem) => {
                            const existingItem = data.items.find((x) => x.idGlobal === createItem.item.idGlobal) as IMovementItemWithRequestLink | undefined;
                            const existingDisplayItem = displayItems.find((x) => x.idGlobal === createItem.displayItem.idGlobal);
                            const requestItemId = existingItem?.idRequestItemGlobal || existingDisplayItem?.idRequestItemGlobal || selectedRequestItem?.idGlobal || '';

                            lotDP.remain(creatorItem.item!.idGlobal, data.idGlobal as string, (remain) => {
                                const displayItemWithAdditionalData: IDisplayItem = {
                                    ...createItem.displayItem,
                                    idRequestItemGlobal: requestItemId,
                                    actualQuantity: remain,
                                    supplierFrom: baseFields.contractorFrom?.displayName || '',
                                    supplierTo: baseFields.contractorTo?.displayName || '',
                                    isKiz: createItem.item.isKiz as boolean,
                                    mnn: creatorItem.item?.mnn || '',
                                    tradeName: creatorItem?.item?.tradeName || '',
                                    cureFormName: creatorItem.item?.cureFormName || '',
                                    dosage: creatorItem.item?.dosage || '',
                                    nFv: creatorItem.item?.nFv || 0
                                };

                                const itemWithRequestLink: IMovementItemWithRequestLink = {
                                    ...createItem.item,
                                    idRequestItemGlobal: requestItemId
                                };

                                setData({ ...data, items: [...data.items.filter((x) => x.idGlobal !== createItem.item.idGlobal), itemWithRequestLink] });
                                setDisplayItems([...displayItems.filter((x) => x.idGlobal !== createItem.displayItem.idGlobal), displayItemWithAdditionalData]);
                                setCreatorItem({ show: false, item: null });
                            })
                        }}
                        cancel={() => setCreatorItem({ show: false, item: null, editItem: null })}
                    />
                )}
            </div>
        </BaseDocumentCreator>
    );
};
